import React, { useEffect, useState } from 'react';
import { Container, Card, Alert, Spinner, Button } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import colors from '../../constants/colors';

const MBWAY_STORAGE_KEY = 'mbway_payment';
const POLL_INTERVAL = 5000;
// Tecto global: passado isto, deixa de se sondar sem declarar o pagamento falhado
// — a confirmação pode ainda chegar pela reconciliação do servidor.
const POLL_TIMEOUT = 15 * 60 * 1000;
// Estados definitivos de falha devolvidos pelo poll; 'paid' é tratado à parte.
// `unknown_gateway_status` NÃO é falha: a API trata estado desconhecido da
// gateway como "voltar a perguntar" — o cron pode ainda confirmar o pagamento.
const FAILED_STATUSES = ['failed', 'expired', 'canceled', 'rejected'];

const MBWayPaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // O refresh perde o location.state: os dados mínimos ficam em sessionStorage
  // para a página sobreviver a um F5 sem declarar o pagamento falhado.
  const [paymentInfo] = useState(() => {
    if (location.state?.orderDetails) return location.state;
    try {
      const saved = sessionStorage.getItem(MBWAY_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const orderDetails = paymentInfo?.orderDetails || null;
  const phoneNumber = paymentInfo?.phoneNumber || '';
  const [status, setStatus] = useState('pending');
  const [error, setError] = useState('');

  useEffect(() => {
    if (paymentInfo) {
      sessionStorage.setItem(MBWAY_STORAGE_KEY, JSON.stringify(paymentInfo));
    }
  }, [paymentInfo]);

  useEffect(() => {
    if (!orderDetails) {
      // Sem encomenda não há nada a sondar: mostrar "aguarde", nunca "falhado" —
      // o pagamento real pode estar pendente de aprovação na app.
      return;
    }

    let timerId = null;
    let navigateTimerId = null;
    let stopped = false;
    const startedAt = Date.now();
    let delay = POLL_INTERVAL;
    let transientErrors = 0;

    const stop = () => {
      stopped = true;
      if (timerId) clearTimeout(timerId);
      if (navigateTimerId) clearTimeout(navigateTimerId);
    };

    const checkPaymentStatus = async () => {
      if (stopped) return;
      try {
        const response = await axiosInstance.get(`/payment/mbway/status/${orderDetails.id}`, {
          headers: { 'X-Order-Token': orderDetails.access_token },
        });
        if (stopped) return; // desmonte durante a sondagem: não tocar em estado nem reagendar
        if (response.status === 'paid') {
          setStatus('paid');
          sessionStorage.removeItem(MBWAY_STORAGE_KEY);
          stop();
          navigateTimerId = setTimeout(() => {
            navigate('/order-success', {
              state: {
                orderDetails: orderDetails,
              }
            });
          }, 3000);
          return;
        } else if (FAILED_STATUSES.includes(response.status)) {
          setStatus('failed');
          setError(`O pagamento ${response.status}. Por favor, tente novamente ou escolha outro método.`);
          sessionStorage.removeItem(MBWAY_STORAGE_KEY);
          stop();
          return;
        }
        // initiated/pending/estado desconhecido: continuar a sondar
        transientErrors = 0;
        delay = POLL_INTERVAL;
        setStatus('pending');
      } catch (err) {
        // Blip de rede/5xx/resposta transient: continuar a sondar com backoff —
        // o pagamento pode estar pago na app à espera de confirmação.
        const transient =
          !err.response ||
          err.status >= 500 ||
          err.response?.status >= 500 ||
          err.data?.transient;
        if (!transient) {
          setStatus('failed');
          setError('Erro ao verificar o estado do pagamento. Por favor, tente novamente.');
          sessionStorage.removeItem(MBWAY_STORAGE_KEY);
          stop();
          return;
        }
        transientErrors += 1;
        delay = transientErrors === 1 ? 2000 : 5000;
      }

      if (Date.now() - startedAt >= POLL_TIMEOUT) {
        setStatus('timeout');
        sessionStorage.removeItem(MBWAY_STORAGE_KEY);
        stop();
        return;
      }
      timerId = setTimeout(checkPaymentStatus, delay);
    };

    checkPaymentStatus();

    return stop;
  }, [orderDetails, navigate]);

  if (!orderDetails) {
    return (
      <Container className="py-5">
        <Card className="text-center">
          <Card.Header style={{ backgroundColor: colors.primary, color: colors.dark }}>
            <h4>Pagamento MB WAY</h4>
          </Card.Header>
          <Card.Body>
            <Alert variant="info">
              <p>
                Não foi possível confirmar o pagamento nesta página. Se já
                confirmaste o pagamento na aplicação MB WAY, a encomenda será
                atualizada em breve.
              </p>
            </Alert>
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/produtos')}
              style={{
                borderColor: colors.secondary,
                color: colors.secondary
              }}
            >
              Continuar a Comprar
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Card className="text-center">
        <Card.Header style={{ backgroundColor: colors.primary, color: colors.dark }}>
          <h4>Pagamento MB WAY</h4>
        </Card.Header>
        <Card.Body>
          {status === 'pending' && (
            <>
              <Alert variant="info">
                <p>Foi enviado um pedido de pagamento para o número {phoneNumber}</p>
                <p>Por favor, confirme o pagamento na sua aplicação MB WAY</p>
              </Alert>
              <Spinner animation="border" role="status" style={{ color: colors.secondary }}>
                <span className="visually-hidden">A aguardar pagamento...</span>
              </Spinner>
              <p className="mt-3">A aguardar confirmação do pagamento...</p>
            </>
          )}

          {status === 'paid' && (
            <Alert variant="success">
              <p>Pagamento confirmado com sucesso!</p>
              <p>Será redirecionado para a página de confirmação...</p>
            </Alert>
          )}

          {status === 'timeout' && (
            <Alert variant="info">
              <p>
                A confirmação do pagamento está a demorar mais do que o esperado.
                Se já confirmaste na aplicação MB WAY, o estado da encomenda será
                atualizado em breve.
              </p>
            </Alert>
          )}

          {status === 'failed' && (
            <>
              <Alert variant="danger">
                <p>{error}</p>
              </Alert>
              <Button
                variant="outline-secondary"
                onClick={() => navigate('/checkout')}
                style={{
                  borderColor: colors.secondary,
                  color: colors.secondary
                }}
              >
                Voltar ao Checkout
              </Button>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default MBWayPaymentPage;
